import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, Plus, Trash, WhatsappLogo, Paperclip, LinkSimple, DownloadSimple, PencilSimple, X } from "@phosphor-icons/react";

const EMPTY_FORM = {
  title: "", subject: "", date: "", duration_min: 60, notes: "",
  student_id: "", whatsapp_notification: false, reminder_rule: "1d",
  links: [""],
};

function isValidUrl(u) {
  if (!u) return false;
  try { new URL(u.startsWith("http") ? u : `https://${u}`); return true; }
  catch { return false; }
}

export default function AgendaPage() {
  const { user } = useAuth();
  const canManage = user?.role !== "student" && user?.role !== "parent";
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const fileRef = useRef(null);

  async function load() { const r = await api.get("/agenda"); setItems(r.data); }
  useEffect(() => {
    load();
    if (user && canManage) {
      api.get("/students").then(r => setStudents(r.data || [])).catch(() => setStudents([]));
    }
  }, [user, canManage]);

  function resetForm() { setForm(EMPTY_FORM); setEditingId(null); }

  async function submit() {
    try {
      const payload = {
        ...form,
        links: (form.links || []).map(s => s.trim()).filter(Boolean),
      };
      if (user.role === "student") {
        payload.student_id = user.id;
      } else if (!payload.student_id) {
        toast.error("Selecione o aluno para esta atividade");
        return;
      }
      // valida URLs
      for (const l of payload.links) {
        if (!isValidUrl(l)) { toast.error(`Link inválido: ${l}`); return; }
      }
      if (editingId) {
        await api.put(`/agenda/${editingId}`, payload);
        toast.success("Atividade atualizada");
      } else {
        await api.post("/agenda", payload);
        toast.success("Atividade criada");
      }
      setOpen(false); resetForm(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  }

  function openEdit(a) {
    setEditingId(a.id);
    setForm({
      title: a.title || "",
      subject: a.subject || "",
      date: a.date || "",
      duration_min: a.duration_min || 60,
      notes: a.notes || "",
      student_id: a.student_id || "",
      whatsapp_notification: !!a.whatsapp_notification,
      reminder_rule: a.reminder_rule || "1d",
      links: (a.links && a.links.length) ? [...a.links] : [""],
    });
    setOpen(true);
  }

  function openNew() { resetForm(); setOpen(true); }

  async function complete(id) { await api.patch(`/agenda/${id}/complete`); load(); }
  async function remove(id) {
    if (!window.confirm("Excluir esta atividade?")) return;
    await api.delete(`/agenda/${id}`); load();
  }

  async function uploadAttachment(aid, file) {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/agenda/${aid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Anexo enviado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar anexo"); }
  }

  async function downloadAttachment(aid, att) {
    try {
      const r = await api.get(`/agenda/${aid}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "anexo"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erro ao baixar"); }
  }

  async function removeAttachment(aid, att_id) {
    if (!window.confirm("Remover este anexo?")) return;
    try { await api.delete(`/agenda/${aid}/attachments/${att_id}`); load(); toast.success("Anexo removido"); }
    catch (e) { toast.error("Erro ao remover"); }
  }

  function setLinkAt(idx, val) {
    setForm(f => { const arr = [...f.links]; arr[idx] = val; return { ...f, links: arr }; });
  }
  function addLinkRow() { setForm(f => ({ ...f, links: [...f.links, ""] })); }
  function removeLinkRow(idx) {
    setForm(f => { const arr = f.links.filter((_, i) => i !== idx); return { ...f, links: arr.length ? arr : [""] }; });
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto space-y-6">
      <header className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Atividades</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">Suas atividades</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="agenda-new" onClick={openNew} className="bg-[#002FA7] hover:bg-[#001f7a] brutal-shadow"><Plus size={16} className="mr-1"/>Nova atividade</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Editar atividade" : "Nova atividade"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {canManage && (
                <div>
                  <Label>Aluno</Label>
                  <Select value={form.student_id} onValueChange={v => setForm({...form, student_id: v})} disabled={!!editingId}>
                    <SelectTrigger data-testid="a-student" className="mt-1">
                      <SelectValue placeholder={students.length ? "Selecione um aluno" : "Nenhum aluno vinculado"} />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name} — {s.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Título</Label><Input data-testid="a-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}/></div>
              <div><Label>Matéria</Label><Input data-testid="a-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}/></div>
              <div><Label>Data</Label><Input data-testid="a-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}/></div>
              <div><Label>Duração (min)</Label><Input type="number" value={form.duration_min} onChange={e => setForm({...form, duration_min: parseInt(e.target.value) || 60})}/></div>
              <div><Label>Observações</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>

              <div className="border-t border-zinc-200 pt-3">
                <Label className="flex items-center gap-1.5"><LinkSimple size={14} weight="bold"/> Links</Label>
                <div className="space-y-2 mt-2">
                  {form.links.map((l, i) => (
                    <div key={i} className="flex gap-2">
                      <Input data-testid={`a-link-${i}`} placeholder="https://..." value={l} onChange={e => setLinkAt(i, e.target.value)} className="flex-1"/>
                      <Button variant="ghost" size="icon" type="button" onClick={() => removeLinkRow(i)} title="Remover link"><X size={14}/></Button>
                    </div>
                  ))}
                  <Button data-testid="a-link-add" variant="outline" size="sm" type="button" onClick={addLinkRow} className="w-full">
                    <Plus size={14} className="mr-1"/> Adicionar link
                  </Button>
                </div>
              </div>

              {canManage && (
                <div className="border-t border-zinc-200 pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="flex items-center gap-1.5">
                        <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
                        Receber notificações via WhatsApp
                      </Label>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Envia alerta antes do prazo. Ao editar, dispara reenvio.</p>
                    </div>
                    <Switch data-testid="a-wa-toggle" checked={form.whatsapp_notification}
                            onCheckedChange={(v) => setForm({...form, whatsapp_notification: v})} />
                  </div>
                  {form.whatsapp_notification && (
                    <div>
                      <Label className="text-xs">Antecedência</Label>
                      <Select value={form.reminder_rule} onValueChange={v => setForm({...form, reminder_rule: v})}>
                        <SelectTrigger data-testid="a-wa-rule" className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1h">1 hora antes</SelectItem>
                          <SelectItem value="1d">1 dia antes</SelectItem>
                          <SelectItem value="2d">2 dias antes</SelectItem>
                          <SelectItem value="3d">3 dias antes</SelectItem>
                          <SelectItem value="1w">1 semana antes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {editingId && canManage && (
                <div className="border-t border-zinc-200 pt-3">
                  <Label className="flex items-center gap-1.5"><Paperclip size={14} weight="bold"/> Anexos</Label>
                  <input ref={fileRef} data-testid="a-file" type="file" hidden
                         onChange={e => { const f = e.target.files?.[0]; if (f) { uploadAttachment(editingId, f); e.target.value = ""; } }}/>
                  <Button variant="outline" size="sm" type="button" className="mt-2 w-full" onClick={() => fileRef.current?.click()}>
                    <Paperclip size={14} className="mr-1"/> Anexar arquivo
                  </Button>
                  <p className="text-[11px] text-zinc-500 mt-1">Salve alterações e reabra a atividade para gerenciar novos anexos.</p>
                </div>
              )}

              <Button data-testid="a-submit" onClick={submit} className="w-full bg-[#002FA7]">
                {editingId ? "Salvar alterações" : "Salvar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      <div className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-100 fade-up">
        {items.length === 0 && <p className="text-center text-sm text-zinc-400 py-16">Nenhuma atividade cadastrada.</p>}
        {items.map(a => (
          <div key={a.id} data-testid={`agenda-item-${a.id}`} className={`p-5 ${a.completed ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-4">
              <button data-testid={`complete-${a.id}`} onClick={() => !a.completed && complete(a.id)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center ${a.completed ? "bg-[#057A55] border-[#057A55] text-white" : "border-zinc-300 hover:border-[#002FA7]"}`}>
                {a.completed && <Check size={14} weight="bold" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${a.completed ? "line-through" : ""}`}>{a.title}</div>
                <div className="text-xs text-zinc-500">{a.subject} • {a.duration_min} min{a.notes ? ` • ${a.notes}` : ""}</div>
              </div>
              <div className="font-mono-num text-sm text-zinc-500 whitespace-nowrap">{a.date}</div>
              {canManage && (
                <Button data-testid={`edit-${a.id}`} variant="ghost" size="sm" onClick={() => openEdit(a)} title="Editar"><PencilSimple size={14}/></Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => remove(a.id)} title="Excluir"><Trash size={14} /></Button>
            </div>
            {((a.links && a.links.length) || (a.attachments && a.attachments.length)) ? (
              <div className="pl-10 mt-3 flex flex-wrap gap-2">
                {(a.links || []).map((l, i) => (
                  <a key={`l${i}`} data-testid={`link-${a.id}-${i}`} href={l} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E5FFF] bg-[#EEF3FF] hover:bg-[#DFE8FF] rounded-full px-3 py-1.5 max-w-full truncate">
                    <LinkSimple size={12} weight="bold" /> <span className="truncate">{l.replace(/^https?:\/\//, "")}</span>
                  </a>
                ))}
                {(a.attachments || []).map((att) => (
                  <span key={att.id} className="inline-flex items-center gap-1 text-xs font-semibold text-[#12143A] bg-[#FFF6EA] border border-[#FFB86B]/40 rounded-full px-2.5 py-1 max-w-full">
                    <Paperclip size={12} weight="bold" className="text-[#B45309]"/>
                    <span className="truncate max-w-[180px]">{att.filename}</span>
                    <button data-testid={`att-dl-${att.id}`} onClick={() => downloadAttachment(a.id, att)} className="ml-1 hover:text-[#1E5FFF]" title="Baixar"><DownloadSimple size={12} weight="bold"/></button>
                    {canManage && (
                      <button onClick={() => removeAttachment(a.id, att.id)} className="ml-1 hover:text-red-500" title="Remover"><X size={12} weight="bold"/></button>
                    )}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
